import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { usersApi } from './users.api';
import type {
  AdminUser,
  AdminUsersFilters,
  AdminUsersPage,
  CreateUserInput,
  UpdateUserInput,
} from '@/types/api';

export const USERS_KEY = ['admin', 'users'] as const;

export function useUsersList(filters: AdminUsersFilters, limit = 50, offset = 0) {
  return useQuery<AdminUsersPage>({
    queryKey: [...USERS_KEY, filters, limit, offset],
    queryFn: () => usersApi.list(filters, limit, offset),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation<AdminUser, Error, CreateUserInput>({
    mutationFn: usersApi.create,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation<AdminUser, Error, { id: string; input: UpdateUserInput }>({
    mutationFn: ({ id, input }) => usersApi.update(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

export function useResetPassword() {
  return useMutation<
    { ok: true; mustChangePassword: true },
    Error,
    { id: string; newPassword: string }
  >({
    mutationFn: ({ id, newPassword }) => usersApi.resetPassword(id, newPassword),
  });
}
